import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ReminderButton } from "../components/ReminderComponents";
import { API_URL } from "../config/api";
import {
  Anchor,
  Ship,
  Plus,
  Upload,
  FileSpreadsheet,
  Trash2,
  RefreshCw,
  Clock,
  Calendar,
  Mail,
  AlertCircle,
  CheckCircle,
  MapPin,
  Loader2,
  ChevronDown,
  ChevronUp,
  Bell,
  X,
  ExternalLink,
} from "lucide-react";


export default function PortTrackingPage() {
  const { getAuthHeaders, user } = useAuth();
  const [ports, setPorts] = useState([]);
  const [trackingEntries, setTrackingEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPort, setSelectedPort] = useState("");
  const [containerNumber, setContainerNumber] = useState("");
  const [notificationEmails, setNotificationEmails] = useState("");
  const [europacificRef, setEuropacificRef] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkContainers, setBulkContainers] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [addingContainer, setAddingContainer] = useState(false);
  const [importingFile, setImportingFile] = useState(false);

  // Fetch ports list
  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const response = await axios.get(`${API_URL}/ports`, { headers: getAuthHeaders() });
        setPorts(response.data);
        if (response.data.length > 0) {
          setSelectedPort(response.data[0].code);
        }
      } catch (error) {
        console.error("Failed to fetch ports:", error);
      }
    };
    fetchPorts();
  }, [getAuthHeaders]);

  // Fetch tracking entries
  const fetchTrackingEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/port-tracking`, { headers: getAuthHeaders() });
      setTrackingEntries(response.data);
    } catch (error) {
      console.error("Failed to fetch tracking entries:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTrackingEntries();
  }, [fetchTrackingEntries]);

  // Add single container
  const handleAddContainer = async (e) => {
    e.preventDefault();
    if (!containerNumber.trim() || !selectedPort) {
      toast.error("Please enter a container number and select a port");
      return;
    }

    setAddingContainer(true);
    try {
      const emails = notificationEmails.split(/[,;\s]+/).filter(e => e.trim());
      
      await axios.post(
        `${API_URL}/port-tracking/add`,
        {
          container_number: containerNumber.trim(),
          port: selectedPort,
          notification_emails: emails,
          europacific_ref: europacificRef || null
        },
        { headers: getAuthHeaders() }
      );

      toast.success(`Container ${containerNumber} added to ${selectedPort} tracking`);
      setContainerNumber("");
      setEuropacificRef("");
      fetchTrackingEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add container");
    } finally {
      setAddingContainer(false);
    }
  };

  // Bulk add containers
  const handleBulkAdd = async () => {
    const containers = bulkContainers.split(/[\n,;]+/).map(c => c.trim()).filter(c => c);
    
    if (containers.length === 0) {
      toast.error("Please enter at least one container number");
      return;
    }

    setAddingContainer(true);
    try {
      const emails = notificationEmails.split(/[,;\s]+/).filter(e => e.trim());
      
      const response = await axios.post(
        `${API_URL}/port-tracking/bulk`,
        {
          container_numbers: containers,
          port: selectedPort,
          notification_emails: emails
        },
        { headers: getAuthHeaders() }
      );

      toast.success(`Added ${response.data.added.length} containers, skipped ${response.data.skipped.length}`);
      setBulkContainers("");
      setShowBulkImport(false);
      fetchTrackingEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add containers");
    } finally {
      setAddingContainer(false);
    }
  };

  // Handle file drop
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer?.files || e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Check if it's a text file (drag/drop text content)
    if (file.type === "text/plain" || file.name.endsWith(".txt") || file.name.endsWith(".csv")) {
      const text = await file.text();
      setBulkContainers(text);
      toast.success("Text file loaded - review and click 'Add All'");
      return;
    }

    // Handle Excel file
    if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
      await handleExcelImport(file);
    } else {
      toast.error("Supported formats: TXT, CSV, XLSX, XLS");
    }
  };

  // Import Excel file
  const handleExcelImport = async (file) => {
    if (!selectedPort) {
      toast.error("Please select a port first");
      return;
    }

    setImportingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("port", selectedPort);
      formData.append("notification_emails", notificationEmails);

      const response = await axios.post(
        `${API_URL}/port-tracking/import-xls`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data"
          }
        }
      );

      toast.success(`Imported ${response.data.added.length} containers from file`);
      fetchTrackingEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import file");
    } finally {
      setImportingFile(false);
    }
  };

  // Check container now
  const handleCheckNow = async (trackingId) => {
    try {
      toast.loading("Checking container status...");
      const response = await axios.post(
        `${API_URL}/port-tracking/${trackingId}/check-now`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.dismiss();
      
      if (response.data.check_result?.found) {
        toast.success(`Status: ${response.data.current_status || 'Found'}`);
      } else {
        toast.info("Container not found at port yet");
      }
      fetchTrackingEntries();
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to check container");
    }
  };

  // Delete entry
  const handleDelete = async (trackingId, containerNumber) => {
    if (!window.confirm(`Stop tracking ${containerNumber}?`)) return;

    try {
      await axios.delete(`${API_URL}/port-tracking/${trackingId}`, { headers: getAuthHeaders() });
      toast.success("Tracking stopped");
      fetchTrackingEntries();
    } catch (error) {
      toast.error("Failed to stop tracking");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "text-green-500";
      case "completed": return "text-blue-500";
      case "expired": return "text-gray-500";
      default: return "text-yellow-500";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Anchor className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Port Container Tracking</h1>
          </div>
          <p className="text-white/80">
            Track containers at Luka Koper, Rijeka APM, and Rijeka AGCT with automatic status updates
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Add Container Form */}
        <div className="card-surface p-6 mb-8">
          <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Add Container to Track
          </h2>

          <form onSubmit={handleAddContainer} className="space-y-6">
            {/* Port Selection */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Port</Label>
                <select
                  value={selectedPort}
                  onChange={(e) => setSelectedPort(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="port-select"
                >
                  {ports.map((port) => (
                    <option key={port.code} value={port.code}>
                      {port.name} ({port.country})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Container Number</Label>
                <Input
                  type="text"
                  placeholder="e.g., TLLU4322906"
                  value={containerNumber}
                  onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
                  className="font-mono"
                  data-testid="container-number-input"
                />
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Europacific Reference</Label>
                <Input
                  type="text"
                  placeholder="Optional reference"
                  value={europacificRef}
                  onChange={(e) => setEuropacificRef(e.target.value)}
                  data-testid="europacific-ref-input"
                />
              </div>
            </div>

            {/* Notification Emails */}
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Notification Emails (comma separated)
              </Label>
              <Input
                type="text"
                placeholder={`${user?.email || 'your@email.com'}, additional@email.com`}
                value={notificationEmails}
                onChange={(e) => setNotificationEmails(e.target.value)}
                data-testid="notification-emails-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Your email ({user?.email}) will always receive notifications
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                type="submit"
                disabled={addingContainer || !containerNumber.trim()}
                className="btn-primary"
                data-testid="add-single-btn"
              >
                {addingContainer ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Add Container
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => setShowBulkImport(!showBulkImport)}
                data-testid="toggle-bulk-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk Import
                {showBulkImport ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
              </Button>
            </div>
          </form>

          {/* Bulk Import Section */}
          <AnimatePresence>
            {showBulkImport && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 pt-6 border-t border-border"
              >
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                  Bulk Import Containers
                </h3>

                {/* Drag & Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive ? "border-primary bg-primary/5" : "border-border"
                  }`}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  data-testid="drop-zone"
                >
                  <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-foreground font-medium mb-2">
                    Drag & drop a file here, or click to browse
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Supported: XLSX, XLS, CSV, TXT (one container number per line)
                  </p>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv,.txt"
                    onChange={handleDrop}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md cursor-pointer hover:bg-primary/90"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {importingFile ? "Importing..." : "Browse Files"}
                  </label>
                </div>

                {/* Manual Text Entry */}
                <div className="mt-6">
                  <Label className="text-sm text-muted-foreground mb-2 block">
                    Or paste container numbers (one per line)
                  </Label>
                  <textarea
                    className="w-full h-32 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                    placeholder="TLLU4322906&#10;MSCU1234567&#10;CMAU9876543"
                    value={bulkContainers}
                    onChange={(e) => setBulkContainers(e.target.value.toUpperCase())}
                    data-testid="bulk-textarea"
                  />
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-sm text-muted-foreground">
                      {bulkContainers.split(/[\n,;]+/).filter(c => c.trim()).length} containers
                    </span>
                    <Button
                      onClick={handleBulkAdd}
                      disabled={addingContainer || !bulkContainers.trim()}
                      data-testid="bulk-add-btn"
                    >
                      {addingContainer ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add All to {selectedPort.replace('_', ' ')}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Tracking Entries List */}
        <div className="card-surface p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Ship className="h-5 w-5 text-primary" />
              Tracked Containers ({trackingEntries.length})
            </h2>
            <Button variant="outline" size="sm" onClick={fetchTrackingEntries}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-2">Loading...</p>
            </div>
          ) : trackingEntries.length === 0 ? (
            <div className="text-center py-12">
              <Anchor className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No containers being tracked</p>
              <p className="text-sm text-muted-foreground">Add a container above to start tracking</p>
            </div>
          ) : (
            <div className="space-y-4">
              {trackingEntries.map((entry) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-border rounded-lg p-4 hover:border-primary/30 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Ship className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-mono text-lg font-bold text-foreground">
                          {entry.container_number}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {entry.port_name}
                          </span>
                          <span className={`flex items-center gap-1 ${getStatusColor(entry.status)}`}>
                            {entry.status === "active" ? (
                              <CheckCircle className="h-3 w-3" />
                            ) : (
                              <AlertCircle className="h-3 w-3" />
                            )}
                            {entry.status}
                          </span>
                        </div>
                        {entry.current_status && (
                          <p className="text-sm mt-1 text-primary font-medium">
                            Port Status: {entry.current_status}
                          </p>
                        )}
                        {entry.requires_manual_check && (
                          <p className="text-sm mt-1 text-amber-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Requires manual verification
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {entry.direct_link && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(entry.direct_link, '_blank')}
                          title="Open tracking page"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      <ReminderButton
                        referenceType="port_tracking"
                        referenceNumber={entry.container_number}
                        getAuthHeaders={getAuthHeaders}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCheckNow(entry.id)}
                        title="Check status now"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                      >
                        {expandedEntry === entry.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(entry.id, entry.container_number)}
                        className="text-red-500 hover:text-red-600"
                        title="Stop tracking"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {expandedEntry === entry.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t border-border"
                      >
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              Tracking Period
                            </p>
                            <p className="text-foreground">
                              {new Date(entry.start_date).toLocaleDateString()} - {new Date(entry.end_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              Last Checked
                            </p>
                            <p className="text-foreground">
                              {entry.last_checked ? new Date(entry.last_checked).toLocaleString() : "Not yet"}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              Notification Emails
                            </p>
                            <p className="text-foreground">
                              {entry.notification_emails?.join(", ") || "None"}
                            </p>
                          </div>
                          {entry.europacific_ref && (
                            <div>
                              <p className="text-muted-foreground">Reference</p>
                              <p className="text-foreground font-mono">{entry.europacific_ref}</p>
                            </div>
                          )}
                        </div>

                        {/* Status History */}
                        {entry.status_history && entry.status_history.length > 0 && (
                          <div className="mt-4">
                            <p className="text-muted-foreground text-sm mb-2">Status History</p>
                            <div className="space-y-2">
                              {entry.status_history.slice(-5).reverse().map((hist, idx) => (
                                <div key={idx} className="text-sm bg-secondary/30 rounded p-2">
                                  <span className="text-muted-foreground">
                                    {new Date(hist.timestamp).toLocaleString()}:
                                  </span>
                                  <span className="text-foreground ml-2">
                                    {hist.old_status || "Initial"} → {hist.new_status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Bell className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-100">Automatic Status Checks</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Containers are automatically checked every 15 minutes. When a status change is detected, 
                notification emails are sent to all configured addresses. Tracking automatically stops 
                10 days after the container's ETA or the configured end date.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
